import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import Logo from '../components/common/Logo'
import MetaTags from '../components/common/MetaTags'
import WheelLoader from '../components/common/WheelLoader'
import ProcessingModal from '../components/resume/ProcessingModal'
import { useAuthModal } from '../contexts/AuthModalContext'
import { useAuth } from '../hooks/useAuth'
import { resumeService } from '../services/resumeService'
import { getProfileUrl } from '../utils/url'

const backgroundImages = [
  '/images/backgrounds/bg-1.png',
  '/images/backgrounds/bg-2.png',
  '/images/backgrounds/bg-3.png',
  '/images/backgrounds/bg-4.png',
  '/images/backgrounds/bg-5.png',
  '/images/backgrounds/bg-6.png',
  '/images/backgrounds/bg-7.png',
  '/images/backgrounds/bg-8.png',
  '/images/backgrounds/bg-9.png',
  '/images/backgrounds/bg-10.png',
]

const stages = [
  { progress: 5, text: 'Uploading your resume...' },
  { progress: 20, text: 'Reading your document...' },
  { progress: 40, text: 'Extracting your experience...' },
  { progress: 60, text: 'Reviewing your education...' },
  { progress: 75, text: 'Discovering your projects...' },
  { progress: 90, text: 'Building your profile...' },
  { progress: 95, text: 'Adding finishing touches...' },
]

export default function LandingPage() {
  const { isAuthenticated, loading } = useAuth()
  const { openAuthModal, isPaused, setIsPaused } = useAuthModal()
  const navigate = useNavigate()

  const [file, setFile] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)

  // Redirect logged-in users to /home
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/home')
    }
  }, [loading, isAuthenticated, navigate])

  // Preload all images
  useEffect(() => {
    const preloadImages = async () => {
      const promises = backgroundImages.map((src) => {
        return new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => resolve()
          img.onerror = () => resolve()
          img.src = src
        })
      })
      await Promise.all(promises)
      setImagesLoaded(true)
    }
    preloadImages()
  }, [])

  // Slideshow timer (4 seconds)
  useEffect(() => {
    if (!imagesLoaded || isPaused) return
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [imagesLoaded, isPaused])

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Please select a PDF file')
        setFile(null)
        return
      }
      setFile(selectedFile)
      setError('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileChange(e.target.files?.[0] || null)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
    handleFileChange(e.dataTransfer.files?.[0] || null)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const simulateProgress = () => {
    let currentStage = 0
    const interval = setInterval(() => {
      if (currentStage < stages.length) {
        setProgress(stages[currentStage].progress)
        setStage(stages[currentStage].text)
        currentStage++
      }
    }, 2500)
    return interval
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isAuthenticated) {
      openAuthModal('signup')
      return
    }

    if (!file) {
      setError('Please select a file')
      return
    }

    setIsProcessing(true)
    setError('')
    const progressInterval = simulateProgress()

    try {
      const response = await resumeService.uploadResume(file)
      clearInterval(progressInterval)
      setProgress(100)
      setStage('Complete!')

      setTimeout(() => {
        window.location.href = getProfileUrl(response.profile.username)
      }, 1000)
    } catch (err: unknown) {
      clearInterval(progressInterval)
      setIsProcessing(false)
      const error = err as { response?: { data?: { error?: string } } }
      setError(error.response?.data?.error || 'Upload failed. Please try again.')
    }
  }

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="mx-auto mb-4">
            <WheelLoader width={100} />
          </div>
          <p className="text-black/60">Loading...</p>
        </div>
      </div>
    )
  }

  // Don't render if authenticated (will redirect)
  if (isAuthenticated) {
    return null
  }

  return (
    <>
      <MetaTags
        title="profile.work - Create Your Professional Profile"
        description="Upload your resume and get a beautiful, shareable profile page in seconds. AI-powered extraction, beautiful templates, and custom themes."
      />
      <ProcessingModal isOpen={isProcessing} progress={progress} stage={stage} />

      {/* Full viewport hero with mesh gradient background */}
      <div
        className="min-h-screen relative overflow-hidden group cursor-pointer"
        onClick={() => setIsPaused(!isPaused)}
      >
        {/* Slideshow Background */}
        <div className="absolute inset-0 -z-10 bg-gray-900">
          {backgroundImages.map((src, index) => (
            <img
              key={src}
              src={src}
              alt=""
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
            />
          ))}
        </div>

        {/* Pause/Play Button */}
        <button
          onClick={(e) => {
            e.stopPropagation()
            setIsPaused(!isPaused)
          }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 w-10 h-10 bg-white/70 backdrop-blur-xl shadow-lg rounded-full flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
        >
          {isPaused ? (
            <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          )}
        </button>

        {/* Logo */}
        <div className="absolute top-6 left-6 z-10" onClick={(e) => e.stopPropagation()}>
          <Logo size="lg" inverted className="drop-shadow-lg" />
        </div>

        {/* Left-aligned content */}
        <div className="flex items-center min-h-screen px-6 sm:px-12 lg:px-24 py-12">
          <div
            className="text-left max-w-sm w-full bg-white/70 backdrop-blur-xl rounded-2xl p-5 sm:p-6 shadow-xl cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl text-black mb-3 tracking-normal"
              style={{ fontFamily: "'Instrument Serif', serif", lineHeight: '0.95' }}
            >
              Stand out.
              <br />
              Don't blend in.
            </h1>

            {/* Subtitle */}
            <div className="mb-6">
              <p className="text-sm sm:text-base text-black/70">
                Create your home on the Internet. Upload your resume and convert it into a website
                within few seconds.
              </p>
              <p className="text-sm sm:text-base text-black/70">No credit card required.</p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-100 border border-red-300">
                <p className="text-xs text-red-800 flex items-center gap-1.5">
                  <svg
                    className="w-3.5 h-3.5 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error}
                </p>
              </div>
            )}

            {/* Upload Form */}
            <form onSubmit={handleSubmit}>
              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-4 cursor-pointer transition-all ${
                  isDragging
                    ? 'border-black bg-black/5'
                    : file
                      ? 'border-black bg-black/5'
                      : 'border-black/20 hover:border-black/40 hover:bg-black/5'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleInputChange}
                  className="hidden"
                />

                {file ? (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-black"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-black truncate">{file.name}</p>
                      <p className="text-xs text-black/60">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFile(null)
                        }}
                        className="text-xs text-black/70 hover:text-black font-medium mt-0.5"
                      >
                        Choose a different file
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-black/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-black"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-black">Drop your resume here</p>
                      <p className="text-xs text-black/60 mt-0.5">
                        or <span className="text-black font-medium">browse</span> to choose a file
                      </p>
                      <p className="text-xs text-black/50 mt-0.5">PDF files up to 10MB</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!file || isProcessing}
                className="w-full mt-4 py-2.5 px-4 bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
              >
                <span className="flex items-center justify-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  Create my website
                </span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}
